import React, {ReactNode, useCallback, useEffect, useRef, useState} from "react"

import id from "@irati/utils/id"
import sentry from "@irati/utils/services/sentry"
import {getErrorMessage} from "@irati/utils/lang/formatError"
import {ApolloLoad, deriveLoad, PBApolloError} from "@irati/utils/services/apollo/helpers/hookShared"
import {hangPromise} from "@irati/utils/promise"

import DefaultLoader from "./Loader"
import DefaultError from "./Error"

export type LoadType = Promise<any> | null | undefined | ApolloLoad | ApolloLoad[]

type InternalLoadType = Promise<any> | null | undefined

interface Props {
  load?: LoadType
  Error?: typeof DefaultError
  Loader?: typeof DefaultLoader | ((props: Props) => React.ReactNode)
  useLoader?: boolean
  isLoading?: boolean
  initLoad?: boolean
  /** if true, renders children without a wrapping `div` */
  inline?: boolean
  children?: ReactNode
}

export interface ErrorProps {
  error?: PBApolloError
  errorMsg: string
}

const isPromise = (promise: any): boolean => {
  return promise && typeof promise === "object" && "then" in promise
}

const Loading = ({
  load = null,
  Error = DefaultError,
  Loader = DefaultLoader,
  useLoader = true,
  initLoad = false,
  isLoading,
  inline,
  children
}: Props) => {
  const currentLoadRef = useRef<InternalLoadType>(null)
  const isMountedRef = useRef(true)

  const [loaded, setLoaded] = useState<boolean>(() => {
    const loadProp = deriveLoad(load)
    const initialLoad = !loadProp && initLoad ? hangPromise() : loadProp
    return !initialLoad || !isPromise(initialLoad)
  })
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [error, setError] = useState<PBApolloError | undefined>(undefined)

  const resetState = useCallback(() => {
    setLoaded(true)
    setErrorMsg(null)
    setError(undefined)
  }, [])

  const handleLoad = useCallback(
    (loadToHandle: InternalLoadType) => {
      if (loadToHandle && isPromise(loadToHandle)) {
        setLoaded(false)
        setErrorMsg(null)
        setError(undefined)
        currentLoadRef.current = loadToHandle

        loadToHandle
          .then(() => {
            const isCurrent = loadToHandle === currentLoadRef.current
            if (isCurrent && isMountedRef.current) {
              resetState()
            }
          })
          .catch((err) => {
            console.error("Loading component error:", err)
            sentry.captureException(err)
            if (isMountedRef.current) {
              setLoaded(false)
              setErrorMsg(getErrorMessage(err))
              setError(err)
            }
          })
      } else {
        resetState()
      }
    },
    [isPromise, resetState]
  )

  useEffect(() => {
    const loadProp = deriveLoad(load)
    const initialLoad = !loadProp && initLoad ? hangPromise() : loadProp
    handleLoad(initialLoad)
  }, [initLoad, load, handleLoad])

  useEffect(() => {
    return () => {
      isMountedRef.current = false
    }
  }, [])

  const idNamespace = id("loading", {load, Error, Loader, useLoader, initLoad, isLoading, inline, children})

  if (errorMsg) {
    return (
      <Error id={id("copy-error", idNamespace)} className="perkboxLoading_error" errorMsg={errorMsg} error={error}>
        {children}
      </Error>
    )
  }

  if ((!loaded && useLoader) || isLoading) {
    return <Loader id={id("loader", idNamespace)}>{children}</Loader>
  }

  return inline ? <>{children}</> : <div id={idNamespace}>{children}</div>
}

export default Loading
